/**
 * Service for Skills and People Occupations API
 * Base URL: https://apicabaza.creago.be/rest/cabaza/skills
 */

import { getAuthHeaders } from './authService';

const API_BASE_URL = 'https://apicabaza.creago.be/rest/cabaza/skills';
const OCCUPATIONS_API_URL = 'https://apicabaza.creago.be/rest/cabaza/occupations';

/**
 * Get skills for an occupation
 * @param {number} occupationId - Occupation ID
 * @param {string} lang - Language code
 * @returns {Promise<Array>} Array of skills
 */
export const getSkillsForOccupation = async (occupationId, lang = 'fr') => {
    try {
        const params = new URLSearchParams({ lang });

        const response = await fetch(`${API_BASE_URL}/occupation/${occupationId}?${params}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();

        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        if (success) {
            return data.map(item => ({
                id: item.id ?? item.ID,
                escoUri: item.escouri ?? item.ESCOURI,
                skillType: item.skilltype ?? item.SKILLTYPE,
                name_fr: item.name_fr ?? item.NAME_FR ?? '',
                name_en: item.name_en ?? item.NAME_EN ?? '',
                name_nl: item.name_nl ?? item.NAME_NL ?? '',
                name_de: item.name_de ?? item.NAME_DE ?? '',
                description_fr: item.description_fr ?? item.DESCRIPTION_FR ?? '',
                description_en: item.description_en ?? item.DESCRIPTION_EN ?? '',
                isEssential: item.isessential ?? item.ISESSENTIAL ?? false
            }));
        }

        return [];
    } catch (error) {
        console.error('Error getting skills for occupation:', error);
        return [];
    }
};

/**
 * Fetch and save ESCO skills for an occupation
 * @param {number} occupationId - Occupation ID
 * @returns {Promise<Object>} Result with imported skills count
 */
export const fetchEscoSkillsForOccupation = async (occupationId) => {
    try {
        const response = await fetch(`${OCCUPATIONS_API_URL}/${occupationId}/esco-skills`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();

        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];
        const essentialCount = result.essentialCount ?? result.ESSENTIALCOUNT ?? 0;
        const optionalCount = result.optionalCount ?? result.OPTIONALCOUNT ?? 0;
        const message = result.message ?? result.MESSAGE ?? '';

        return {
            success,
            data,
            essentialCount,
            optionalCount,
            message
        };
    } catch (error) {
        console.error('Error fetching ESCO skills:', error);
        return { success: false, data: [], essentialCount: 0, optionalCount: 0, message: error.message };
    }
};

/**
 * Get skills for a person
 * @param {string} peopleUuid - Person UUID
 * @param {number} occupationId - Optional occupation filter
 * @param {string} lang - Language code
 * @returns {Promise<Object>} Object with skills array and skillIds
 */
export const getPeopleSkills = async (peopleUuid, occupationId = null, lang = 'fr') => {
    try {
        const params = new URLSearchParams({ lang });
        if (occupationId) {
            params.append('occupationId', occupationId);
        }

        const response = await fetch(`${API_BASE_URL}/people/${peopleUuid}?${params}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();

        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];
        const skillIds = result.skillIds ?? result.SKILLIDS ?? [];

        if (success) {
            return {
                skills: data.map(item => ({
                    id: item.id ?? item.ID,
                    escoUri: item.escouri ?? item.ESCOURI,
                    skillType: item.skilltype ?? item.SKILLTYPE,
                    name_fr: item.name_fr ?? item.NAME_FR ?? '',
                    name_en: item.name_en ?? item.NAME_EN ?? '',
                    occupationId: item.occupationid ?? item.OCCUPATIONID
                })),
                skillIds: skillIds.map(id => parseInt(id, 10))
            };
        }

        return { skills: [], skillIds: [] };
    } catch (error) {
        console.error('Error getting people skills:', error);
        return { skills: [], skillIds: [] };
    }
};

/**
 * Toggle a skill for a person (add or remove)
 * @param {string} peopleUuid - Person UUID
 * @param {number} skillId - Skill ID
 * @param {string} action - 'add' or 'remove'
 * @param {number} occupationId - Optional occupation ID
 * @returns {Promise<Object>} Result
 */
export const togglePeopleSkill = async (peopleUuid, skillId, action = 'add', occupationId = null) => {
    try {
        const body = { skillId, action };
        if (occupationId) {
            body.occupationId = occupationId;
        }

        const response = await fetch(`${API_BASE_URL}/people/${peopleUuid}/toggle`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(body)
        });

        const result = await response.json();

        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error toggling people skill:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Get occupations for a person
 * @param {string} peopleUuid - Person UUID
 * @param {string} lang - Language code
 * @returns {Promise<Array>} Array of occupations
 */
export const getPeopleOccupations = async (peopleUuid, lang = 'fr') => {
    try {
        const params = new URLSearchParams({ lang });

        const response = await fetch(`${API_BASE_URL}/people-occupations/${peopleUuid}?${params}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();

        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        if (success) {
            return data.map(item => ({
                id: item.id ?? item.ID,
                escoUri: item.escouri ?? item.ESCOURI,
                name_fr: item.name_fr ?? item.NAME_FR ?? '',
                name_en: item.name_en ?? item.NAME_EN ?? '',
                name_nl: item.name_nl ?? item.NAME_NL ?? '',
                name_de: item.name_de ?? item.NAME_DE ?? '',
                isPrimary: item.isprimary ?? item.ISPRIMARY ?? false,
                createdAt: item.createdat ?? item.CREATEDAT
            }));
        }

        return [];
    } catch (error) {
        console.error('Error getting people occupations:', error);
        return [];
    }
};

/**
 * Add an occupation to a person
 * @param {string} peopleUuid - Person UUID
 * @param {number} occupationId - Occupation ID
 * @param {boolean} isPrimary - Set as primary occupation
 * @returns {Promise<Object>} Result
 */
export const addPeopleOccupation = async (peopleUuid, occupationId, isPrimary = false) => {
    try {
        const response = await fetch(`${API_BASE_URL}/people-occupations/${peopleUuid}`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ occupationId, isPrimary })
        });

        const result = await response.json();

        return {
            success: result.success ?? result.SUCCESS ?? false,
            id: result.id ?? result.ID ?? 0,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error adding people occupation:', error);
        return { success: false, id: 0, message: error.message };
    }
};

/**
 * Remove an occupation from a person
 * @param {string} peopleUuid - Person UUID
 * @param {number} occupationId - Occupation ID
 * @returns {Promise<Object>} Result
 */
export const removePeopleOccupation = async (peopleUuid, occupationId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/people-occupations/${peopleUuid}/${occupationId}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();

        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error removing people occupation:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Set primary occupation for a person
 * @param {string} peopleUuid - Person UUID
 * @param {number} occupationId - Occupation ID
 * @returns {Promise<Object>} Result
 */
export const setPrimaryOccupation = async (peopleUuid, occupationId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/people-occupations/${peopleUuid}/primary/${occupationId}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();

        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error setting primary occupation:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Fill missing translation for a skill
 * @param {number} skillId - Skill ID
 * @param {string} lang - Language code
 * @returns {Promise<Object>} Result with translated name
 */
export const fillSkillTranslation = async (skillId, lang = 'fr') => {
    try {
        const response = await fetch(`${API_BASE_URL}/${skillId}/fill-translation`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ lang })
        });

        const result = await response.json();

        return {
            success: result.success ?? result.SUCCESS ?? false,
            name: result.name ?? result.NAME ?? '',
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error filling skill translation:', error);
        return { success: false, name: '', message: error.message };
    }
};
